/**
 * <ReferralDashboardPage> — le tableau de parrainage (page).
 *
 * Ce que le cœur du header ouvre (`referrer/dashboard`) : le lien de la famille, ses canaux de
 * partage, ses quatre compteurs, les trois étapes du programme et ses conditions. Le legacy
 * (`src/admin/ReferralCodeScreen`) posait tout ça dans une carte à dégradé, avec une animation de
 * confettis à la copie ; ici la page tient par sa hiérarchie — une seule surface en relief, celle
 * qui porte le lien.
 *
 * Composition pure : les chiffres, le lien et le montant arrivent en props, les effets (copier,
 * partager, réessayer) sont injectés. Le chargement et l'erreur passent par `<PageTemplate>`.
 */
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Card } from "../../molecules/Card"
import { Header } from "../../molecules/Header"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { PageTemplate, PARENT_CONTENT_WIDTH } from "../../templates/PageTemplate"
import { ReferralHowItWorks } from "../../organisms/ReferralHowItWorks"
import { ReferralShare, type ReferralShareAction } from "../../organisms/ReferralShare"
import { ReferralStatsCard } from "../../organisms/ReferralStatsCard"
import { ReferralTermsCard } from "../../organisms/ReferralTermsCard"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { columnStyles } from "../../atoms/containers/columns"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { type UIReferralDashboard } from "../../../ports/primary/ui/types"
import { ReferralDashboardSkeleton } from "./ReferralDashboardSkeleton"

export type ReferralDashboardPageProps = {
    /** Le tableau — `null` tant qu'il n'y a rien à montrer (chargement, erreur). */
    dashboard?: UIReferralDashboard | null
    /** Les canaux de partage (WhatsApp, Telegram, e-mail) — injectés, comme dans la modale. */
    shareActions?: ReferralShareAction[]
    onCopyLink?: () => void
    /** Échec d'un canal de partage — « WhatsApp n'est pas installé ». */
    shareErrorMessage?: string | null
    /**
     * L'identité du parrain, déjà écrite (« Marie Lefevre - marie.lefevre@example.com »). Elle
     * n'a de sens que sur la vue par lien, celle qu'un admin ouvre : présente, elle change aussi
     * le titre de la page, comme le legacy.
     */
    referrerLabel?: string | null
    /** Ouvre les conditions au premier rendu — le repli lui-même reste à la charge du composant. */
    defaultTermsExpanded?: boolean
    onToggleTerms?: (expanded: boolean) => void
    onClose?: () => void
    loading?: boolean
    error?: unknown
    onRetry?: () => void
    testID?: string
}

export function ReferralDashboardPage(props: ReferralDashboardPageProps) {
    const {
        dashboard,
        shareActions,
        onCopyLink,
        shareErrorMessage,
        referrerLabel,
        defaultTermsExpanded,
        onToggleTerms,
        onClose,
        loading,
        error,
        onRetry,
        testID,
    } = props

    const header = (
        <Header
            title={referrerLabel != null && referrerLabel !== "" ? "Parrainage" : "Mon code de parrainage"}
            left={
                onClose != null ? (
                    <IconButton iconName="times" appearance="transparent" iconColor="primary" onPress={onClose} />
                ) : undefined
            }
        />
    )

    return (
        <PageTemplate
            testID={testID}
            header={header}
            loading={loading}
            skeleton={<ReferralDashboardSkeleton />}
            error={error}
            onRetry={onRetry}
            padding="4"
            maxContentWidth={PARENT_CONTENT_WIDTH}
        >
            {dashboard != null && (
                <Stack gap="4">
                    {referrerLabel != null && referrerLabel !== "" && (
                        <Text size="text-sm" color="text-muted">
                            {referrerLabel}
                        </Text>
                    )}

                    {/* Grand écran : le lien et ses chiffres à gauche, le programme à droite. */}
                    <View style={columnStyles.columns}>
                        <Stack gap="4" style={columnStyles.column}>
                            {/* La seule surface en relief de la page : ce pour quoi le parent l'a ouverte. */}
                            <Card p="0" style={styles.shareCard}>
                                <ReferralShare
                                    link={{ url: dashboard.url, rewardLabel: dashboard.rewardLabel }}
                                    shareActions={shareActions}
                                    onCopyLink={onCopyLink}
                                    errorMessage={shareErrorMessage}
                                    showBenefit={false}
                                />
                            </Card>

                            <Stack gap="2">
                                <SectionLabel>Statistiques de votre lien</SectionLabel>
                                <ReferralStatsCard stats={dashboard.stats} />
                            </Stack>
                        </Stack>

                        <Stack gap="4" style={columnStyles.column}>
                            <Stack gap="2">
                                <SectionLabel>Comment ça marche</SectionLabel>
                                <ReferralHowItWorks rewardLabel={dashboard.rewardLabel} />
                            </Stack>

                            <ReferralTermsCard defaultExpanded={defaultTermsExpanded} onToggle={onToggleTerms} />
                        </Stack>
                    </View>
                </Stack>
            )}
        </PageTemplate>
    )
}

const styles = StyleSheet.create(() => ({
    /** Le bandeau marine occupe le haut de la carte : sans découpe, ses angles dépassent. */
    shareCard: { overflow: "hidden" },
}))
